import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Popover,
  Tooltip,
  Typography
} from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const CoachIcon = props => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    <path d="M8 9h8" />
    <path d="M8 13h5" />
  </svg>
);

const OpenActionIcon = props => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M5 12h12" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

const formatDate = value => {
  if (!value) return "Ahora";
  try {
    return new Date(value).toLocaleString("es-CO");
  } catch (_error) {
    return value;
  }
};

const getPreview = note => {
  const meta = note?.metadataJson || {};
  const body = String(meta.messageBody || "").trim();
  if (body) {
    return body.slice(0, 140);
  }
  if (meta.mediaType) {
    return `Mensaje de tipo ${meta.mediaType}`;
  }
  return "Mensaje sin vista previa";
};

const getTicketLabel = note => {
  const numericId = Number(note?.ticket?.id || 0);
  if (numericId) {
    return `Ticket #${numericId}`;
  }

  if (note?.ticket?.uuid) {
    return `Ticket ${String(note.ticket.uuid).slice(0, 8)}`;
  }

  return "Ticket";
};

const coachFloatingPulse = {
  "0%": {
    transform: "translateY(0) scale(1)",
    boxShadow: "0 14px 28px rgba(214, 40, 40, 0.30), 0 0 0 0 rgba(255, 77, 79, 0.34)",
    opacity: 1,
  },
  "30%": {
    transform: "translateY(-3px) scale(1.03)",
    boxShadow: "0 18px 34px rgba(214, 40, 40, 0.40), 0 0 0 10px rgba(255, 77, 79, 0.16)",
    opacity: 1,
  },
  "55%": {
    transform: "translateY(-1px) scale(1.01)",
    boxShadow: "0 18px 34px rgba(214, 40, 40, 0.34), 0 0 0 18px rgba(255, 77, 79, 0.06)",
    opacity: 0.98,
  },
  "100%": {
    transform: "translateY(0) scale(1)",
    boxShadow: "0 14px 28px rgba(214, 40, 40, 0.30), 0 0 0 0 rgba(255, 77, 79, 0)",
    opacity: 1,
  }
};

const coachFloatingFlash = {
  "0%": { filter: "brightness(1)" },
  "50%": { filter: "brightness(1.12)" },
  "100%": { filter: "brightness(1)" }
};

export default function MessageCoachPopover() {
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notes, setNotes] = useState([]);
  const [popupNote, setPopupNote] = useState(null);
  const [pulseOn, setPulseOn] = useState(true);
  const notifiedNotesRef = useRef(new Set());
  const floatingCoachRef = useRef(null);
  const promptShownRef = useRef(false);
  const [showCoachBanner, setShowCoachBanner] = useState(true);

  const activeCount = useMemo(
    () => notes.filter(item => item.status !== "resolved").length,
    [notes]
  );

  const unseenCount = useMemo(
    () => notes.filter(item => !item.readAt && item.status !== "resolved").length,
    [notes]
  );

  const loadNotes = async () => {
    try {
      const { data } = await api.get("/message-coach-notes/me");
      setNotes(Array.isArray(data) ? data : []);
    } catch (error) {
      toastError(error);
    }
  };

  useEffect(() => {
    if (!user?.id) return undefined;
    loadNotes();
    const interval = setInterval(loadNotes, 20000);
    return () => clearInterval(interval);
  }, [user?.id]);

  useEffect(() => {
    const pending = notes.filter(item => !item.readAt && item.status !== "resolved");
    if (!pending.length) {
      setPopupNote(null);
      if (!activeCount) {
        promptShownRef.current = false;
      }
      return;
    }

    setPopupNote(current => {
      if (current && pending.some(item => item.id === current.id)) {
        return current;
      }
      return pending[0];
    });
  }, [notes, activeCount]);

  useEffect(() => {
    if (!activeCount) {
      setPulseOn(false);
      return undefined;
    }

    setPulseOn(true);
    const interval = setInterval(() => {
      setPulseOn(prev => !prev);
    }, 850);

    return () => clearInterval(interval);
  }, [activeCount]);

  useEffect(() => {
    if (!activeCount) {
      setShowCoachBanner(true);
      return;
    }

    setShowCoachBanner(true);
  }, [activeCount]);

  useEffect(() => {
    if (!activeCount || promptShownRef.current) {
      return;
    }

    promptShownRef.current = true;

    toast.warn(
      activeCount === 1
        ? "Tiene 1 pendiente de coach por revisar"
        : `Tiene ${activeCount} pendientes de coach por revisar`,
      {
        autoClose: 9000,
        onClick: () => {
          if (floatingCoachRef.current) {
            setAnchorEl(floatingCoachRef.current);
          }
        }
      }
    );
  }, [activeCount]);

  useEffect(() => {
    notes.forEach(note => {
      if (!note || note.status === "resolved" || note.readAt) {
        return;
      }

      if (notifiedNotesRef.current.has(note.id)) {
        return;
      }

      notifiedNotesRef.current.add(note.id);

      toast.info(
        `Coach pendiente en ${getTicketLabel(note)}`,
        {
          autoClose: 9000,
          onClick: () => openNote(note)
        }
      );
    });
  }, [notes]);

  useEffect(() => {
    if (!socket || !user?.companyId || !user?.id) return undefined;

    const companyId = user.companyId;

    const onCoachNote = payload => {
      const note = payload?.note;
      const targetUserId = Number(payload?.targetUserId || note?.targetUserId || 0);

      if (!note || targetUserId !== Number(user.id || 0)) {
        return;
      }

      setNotes(prev => {
        const next = Array.isArray(prev) ? [...prev] : [];
        const idx = next.findIndex(item => Number(item.id) === Number(note.id));
        if (idx >= 0) {
          next[idx] = note;
        } else {
          next.unshift(note);
        }
        return next.filter(item => item.status !== "resolved");
      });

      if (String(note.status || "open") !== "resolved" && !note.readAt) {
        setPopupNote(note);

        if (!notifiedNotesRef.current.has(note.id)) {
          notifiedNotesRef.current.add(note.id);
          toast.info(
            `${note?.authorUser?.name || "Administracion"} le dejo una anotacion de coach`,
            {
              autoClose: 9000,
              onClick: () => openNote(note)
            }
          );
        }
      }
    };

    socket.on(`company-${companyId}-message-coach-note`, onCoachNote);
    return () => socket.off(`company-${companyId}-message-coach-note`, onCoachNote);
  }, [socket, user?.companyId, user?.id]);

  const openNote = async note => {
    try {
      localStorage.setItem("soluwhats:pendingCoachMessage", JSON.stringify({
        ticketUuid: note?.ticket?.uuid || null,
        messageId: Number(note?.messageId || 0),
        noteId: Number(note?.id || 0),
        createdAt: Date.now()
      }));

      if (!note?.readAt) {
        await api.post(`/message-coach-notes/${note.id}/read`);
      }

      setNotes(prev =>
        prev.map(item =>
          item.id === note.id
            ? { ...item, readAt: item.readAt || new Date().toISOString() }
            : item
        )
      );
      notifiedNotesRef.current.add(note.id);
      setPopupNote(current => (current?.id === note.id ? null : current));
      setAnchorEl(null);

      if (note?.ticket?.uuid) {
        history.push(`/tickets/${note.ticket.uuid}`);
      }
    } catch (error) {
      toastError(error);
    }
  };

  const openPendingCoachList = () => {
    setPopupNote(null);
    setShowCoachBanner(false);
    if (floatingCoachRef.current) {
      setAnchorEl(floatingCoachRef.current);
    }
  };

  return (
    <>
      <Tooltip title={activeCount ? `${activeCount} anotacion(es) pendientes del coach` : "Coach operativo"}>
        <IconButton
          onClick={e => setAnchorEl(e.currentTarget)}
          style={{
            color: activeCount ? "#fff7d6" : "white",
            background: activeCount ? (pulseOn ? "rgba(214, 40, 40, 0.92)" : "rgba(255, 145, 77, 0.92)") : "transparent",
            border: activeCount ? "1px solid rgba(255, 240, 200, 0.75)" : "1px solid transparent",
            boxShadow: activeCount ? (pulseOn ? "0 0 0 5px rgba(214, 40, 40, 0.18)" : "0 0 0 2px rgba(255, 145, 77, 0.14)") : "none",
            transform: activeCount ? (pulseOn ? "scale(1.08)" : "scale(1)") : "scale(1)",
            transition: "all 180ms ease"
          }}
        >
          <Badge badgeContent={activeCount} color="secondary">
            <CoachIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      {activeCount > 0 && showCoachBanner && (
        <div
          ref={floatingCoachRef}
          onClick={openPendingCoachList}
          role="button"
          style={{
            position: "fixed",
            top: 66,
            left: "50%",
            transform: pulseOn ? "translateX(-50%) translateY(-2px) scale(1.02)" : "translateX(-50%) translateY(0) scale(1)",
            zIndex: 1700,
            minWidth: 320,
            maxWidth: 520,
            borderRadius: 18,
            padding: "12px 16px",
            cursor: "pointer",
            color: "#ffffff",
            background: pulseOn
              ? "linear-gradient(135deg, #c1121f 0%, #e5383b 48%, #ff7b00 100%)"
              : "linear-gradient(135deg, #a4133c 0%, #d62828 48%, #f77f00 100%)",
            border: "1px solid rgba(255, 244, 214, 0.72)",
            animation: "coachFloatingPulse 1.2s ease-in-out infinite, coachFloatingFlash 1.4s ease-in-out infinite",
            boxShadow: pulseOn
              ? "0 18px 34px rgba(214, 40, 40, 0.42), 0 0 0 8px rgba(214, 40, 40, 0.18)"
              : "0 14px 28px rgba(255, 123, 0, 0.26), 0 0 0 3px rgba(255, 145, 77, 0.14)",
            transition: "all 180ms ease",
          }}
        >
          <style>
            {`
              @keyframes coachFloatingPulse {
                0% {
                  transform: ${coachFloatingPulse["0%"].transform};
                  box-shadow: ${coachFloatingPulse["0%"].boxShadow};
                  opacity: ${coachFloatingPulse["0%"].opacity};
                }
                30% {
                  transform: ${coachFloatingPulse["30%"].transform};
                  box-shadow: ${coachFloatingPulse["30%"].boxShadow};
                  opacity: ${coachFloatingPulse["30%"].opacity};
                }
                55% {
                  transform: ${coachFloatingPulse["55%"].transform};
                  box-shadow: ${coachFloatingPulse["55%"].boxShadow};
                  opacity: ${coachFloatingPulse["55%"].opacity};
                }
                100% {
                  transform: ${coachFloatingPulse["100%"].transform};
                  box-shadow: ${coachFloatingPulse["100%"].boxShadow};
                  opacity: ${coachFloatingPulse["100%"].opacity};
                }
              }

              @keyframes coachFloatingFlash {
                0% { filter: ${coachFloatingFlash["0%"].filter}; }
                50% { filter: ${coachFloatingFlash["50%"].filter}; }
                100% { filter: ${coachFloatingFlash["100%"].filter}; }
              }
            `}
          </style>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 14,
                height: 14,
                borderRadius: "50%",
                background: "#fff4d6",
                boxShadow: pulseOn ? "0 0 0 10px rgba(255, 244, 214, 0.18)" : "0 0 0 0 rgba(255, 244, 214, 0)",
                transition: "all 180ms ease",
                flexShrink: 0,
              }}
            />
            <div style={{ minWidth: 0 }}>
              <Typography style={{ fontWeight: 900, fontSize: 14, color: "#fffaf0", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Coach pendiente
              </Typography>
              <Typography style={{ fontSize: 13, color: "#fff7e1", fontWeight: 600 }}>
                {activeCount === 1
                  ? "Tiene 1 observacion pendiente por atender"
                  : `Tiene ${activeCount} observaciones pendientes por atender`}
              </Typography>
            </div>
            <div
              style={{
                marginLeft: "auto",
                minWidth: 36,
                height: 36,
                borderRadius: 999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#fff6dd",
                color: "#8d0801",
                fontWeight: 900,
                fontSize: 16,
                flexShrink: 0,
              }}
            >
              {activeCount}
            </div>
          </div>
          <Typography style={{ marginTop: 8, fontSize: 12, color: "#fff3d6", fontWeight: 600 }}>
            Toque aqui para abrir los pendientes y responder el coach.
          </Typography>
        </div>
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <div style={{ width: 380, maxHeight: 420, overflowY: "auto", padding: 8 }}>
          <Typography variant="subtitle1" style={{ fontWeight: 700, padding: 8 }}>
            Coach operativo
          </Typography>
          <List dense>
            {notes.map(item => (
              <ListItem
                key={item.id}
                button
                onClick={() => openNote(item)}
                style={{
                  borderRadius: 10,
                  marginBottom: 6,
                  background: !item.readAt && item.status !== "resolved" ? "rgba(255,193,7,0.14)" : "transparent",
                  alignItems: "center",
                  gap: 10
                }}
              >
                <ListItemText
                  primary={`De ${item.authorUser?.name || "administracion"}`}
                  secondary={
                    <>
                      <span style={{ fontWeight: 600 }}>
                        {getTicketLabel(item)}
                      </span>
                      <br />
                      <span>{String(item.note || "").slice(0, 120)}</span>
                      <br />
                      <span>{formatDate(item.createdAt)}</span>
                    </>
                  }
                />
                <div
                  style={{
                    minWidth: 78,
                    height: 34,
                    borderRadius: 999,
                    padding: "0 12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                    fontSize: 12,
                    fontWeight: 800,
                    letterSpacing: "0.02em",
                    color: !item.readAt && item.status !== "resolved" ? "#7a3e00" : "#24507a",
                    background: !item.readAt && item.status !== "resolved"
                      ? "linear-gradient(135deg, rgba(255, 214, 102, 0.92) 0%, rgba(255, 193, 7, 0.88) 100%)"
                      : "linear-gradient(135deg, rgba(226, 239, 251, 0.92) 0%, rgba(201, 224, 245, 0.9) 100%)",
                    border: "1px solid rgba(36, 80, 122, 0.16)",
                    boxShadow: "0 8px 18px rgba(22, 49, 73, 0.12)",
                    flexShrink: 0
                  }}
                >
                  <OpenActionIcon />
                  Abrir
                </div>
              </ListItem>
            ))}
            {!notes.length && (
              <Typography variant="body2" color="textSecondary" style={{ padding: 12 }}>
                No hay anotaciones pendientes del coach.
              </Typography>
            )}
          </List>
        </div>
      </Popover>

      <Dialog
        open={Boolean(popupNote)}
        fullWidth
        maxWidth="sm"
        disableEscapeKeyDown
        onClose={() => {}}
      >
        <DialogTitle>Coach operativo</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle1" style={{ marginBottom: 8, fontWeight: 700 }}>
            {popupNote?.authorUser?.name || "Administracion"} le dejo una anotacion
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 10 }}>
            Esta observacion esta dirigida a usted y no es visible para el cliente.
          </Typography>
          <Typography variant="body1" style={{ whiteSpace: "pre-line", marginBottom: 12 }}>
            {popupNote?.note}
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ whiteSpace: "pre-line" }}>
            Mensaje base: {getPreview(popupNote)}
          </Typography>
          <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 12 }}>
            Recibida: {formatDate(popupNote?.createdAt)}
          </Typography>
        </DialogContent>
        <DialogActions>
          {unseenCount > 1 && (
            <Button onClick={openPendingCoachList} color="default">
              Ver todos ({unseenCount})
            </Button>
          )}
          <Button onClick={() => openNote(popupNote)} color="primary" variant="contained">
            Ir al chat
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
